import { useState } from 'react';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { FormField, Input, Label } from '../ui/input';
import { PUBLIC_TAGS } from '../../types';
import type { Tag } from '../../types';

interface TagsManagerProps {
  tags: Tag[];
  onAdd: (data: { name: string; icon: string }) => Promise<void>;
  onUpdate: (id: string, data: { name: string; icon: string }) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function TagsManager({ tags, onAdd, onUpdate, onDelete }: TagsManagerProps) {
  const [newIcon, setNewIcon] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editIcon, setEditIcon] = useState('');
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newIcon.trim()) return;
    setCreating(true);
    try {
      await onAdd({ name: newName.trim(), icon: newIcon.trim() });
      setNewName('');
      setNewIcon('');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (tag: Tag) => {
    setEditingId(tag.id);
    setEditIcon(tag.icon);
    setEditName(tag.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditIcon('');
    setEditName('');
  };

  const handleSaveEdit = async (id: string) => {
    if (!editName.trim() || !editIcon.trim()) return;
    setSaving(true);
    try {
      await onUpdate(id, { name: editName.trim(), icon: editIcon.trim() });
      cancelEdit();
    } finally {
      setSaving(false);
    }
  };

  const publicTagIds = new Set(PUBLIC_TAGS.map((t) => t.id));
  const userTags = tags.filter((t) => !publicTagIds.has(t.id));

  return (
    <div className="flex flex-col gap-6">
      {/* Public tags (read-only) */}
      <div className="rounded-xl border border-border bg-white shadow-sm">
        <p className="border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Tags públicas · {PUBLIC_TAGS.length}
        </p>
        <ul className="divide-y divide-border">
          {PUBLIC_TAGS.map((tag) => (
            <li key={tag.id} className="flex items-center gap-3 p-3">
              <span className="text-lg">{tag.icon}</span>
              <span className="flex-1 text-sm font-medium text-foreground">
                {tag.name}
              </span>
              <span className="text-xs text-muted-foreground">pública</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Create form */}
      <div className="rounded-xl border border-border bg-white p-4 shadow-sm">
        <h3 className="mb-4 text-sm font-semibold text-foreground">Nova tag</h3>
        <form onSubmit={handleCreate} className="flex items-end gap-3">
          <FormField className="w-20 shrink-0">
            <Label htmlFor="new-icon">Emoji</Label>
            <Input
              id="new-icon"
              value={newIcon}
              onChange={(e) => setNewIcon(e.target.value)}
              placeholder="🛒"
              maxLength={4}
            />
          </FormField>
          <FormField className="flex-1">
            <Label htmlFor="new-name">Nome</Label>
            <Input
              id="new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ex: Mercado"
            />
          </FormField>
          <Button
            type="submit"
            disabled={!newName.trim() || !newIcon.trim() || creating}
            className="shrink-0"
          >
            Criar
          </Button>
        </form>
      </div>

      {/* User tags list */}
      <div className="rounded-xl border border-border bg-white shadow-sm">
        <p className="border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Suas tags · {userTags.length}
        </p>
        {userTags.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">Nenhuma tag criada ainda.</p>
        ) : (
          <ul className="divide-y divide-border">
            {userTags.map((tag) =>
              editingId === tag.id ? (
                <li key={tag.id} className="flex items-center gap-3 p-3">
                  <Input
                    value={editIcon}
                    onChange={(e) => setEditIcon(e.target.value)}
                    className="w-16 shrink-0"
                    maxLength={4}
                  />
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSaveEdit(tag.id)}
                    disabled={!editName.trim() || !editIcon.trim() || saving}
                    className="p-1 text-success hover:text-success"
                  >
                    <Check className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={cancelEdit}
                    className="p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-4" />
                  </Button>
                </li>
              ) : (
                <li key={tag.id} className="flex items-center gap-3 p-3">
                  <span className="text-lg">{tag.icon}</span>
                  <span className="flex-1 text-sm font-medium text-foreground">
                    {tag.name}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(tag)}
                    className="p-1 text-muted-foreground hover:text-primary"
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(tag.id)}
                    className="p-1 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              )
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
